import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import CheckmarkFilled32 from '@carbon/icons-react/es/checkmark--filled/32';
import Login32 from '@carbon/icons-react/es/login/32';
import MagicWandFilled32 from '@carbon/icons-react/es/magic-wand--filled/32';
import { OktaContext } from '@okta/okta-react';
import { Navigate, useLocationState } from '@rocketlane/rocket-router';
import { useMutation } from '@tanstack/react-query';
import { useBoolean } from 'ahooks';
import { Result as AntResult } from 'antd';
import { useState, useContext } from 'react';
import styled from 'styled-components';
import { UserAPI } from 'api/user';
import { IconShell } from 'components/atoms/icon-shell';
import { Button } from 'components/facade/button';
import { Input as InputAtom } from 'components/facade/input';
import { Page as PageAtom } from 'components/molecules/page';
import { ProductiveHeading04, ProductiveHeading05 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useTranslation } from 'hooks/use-translation';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;
const Page = styled(PageAtom)`
  padding: 48px;
`;
const Input = styled(InputAtom)`
  width: 360px;
  max-width: 360px;
`;

const Email = styled.strong`
  ${ProductiveHeading04};
`;

const Result = styled(AntResult)`
  & > * {
    text-align: left;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--layout-05);
`;

const Title = styled.div`
  ${ProductiveHeading05};
`;

export enum MagicLinkMessageType {
  NotSignedIn = 'not-signed',
  Expired = 'expired',
}

const GenerateNotifyMessage = () => (
  <Container>
    <Title>{useTranslation('magic_link.generate.success.help')}</Title>
  </Container>
);

const GenerateLinkForm = ({ onSubmit, isLoading }) => {
  const [email, setEmail] = useState('');

  return (
    <Wrapper>
      <Input
        disabled={isLoading}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={useTranslation('magic_link.email.placeholder')}
        onPressEnter={(e) => onSubmit(e.currentTarget.value)}
      />
      <Button
        loading={isLoading}
        type="primary"
        key="new-magic-link"
        icon={<ArrowRight16 />}
        iconPlacement="right"
        onClick={() => onSubmit(email)}
      >
        {useTranslation('magic_link.invite')}
      </Button>
    </Wrapper>
  );
};

export const MagicLinkGeneratePage = () => {
  const state = useLocationState() as {
    messageType: MagicLinkMessageType;
  } | null;
  const messageType = state ? state.messageType : null;
  const [email, setEmail] = useState('');
  const request = (_email: string) => UserAPI.getMagicLink(_email);
  const [isSuccess, { setTrue }] = useBoolean(false);
  const { isLoading: loading, mutateAsync: run } = useMutation(request, {
    onSuccess: () => {
      setTrue();
    },
  });
  const oktaContext = useContext(OktaContext);
  const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
  const authState = oktaContext?.authState ?? {
    isAuthenticated: customIsAuthenticated,
  };
  const expiredTitle = useTranslation('magic_link.expired.title');
  const notSignedInTitle = useTranslation('magic_link.not_signed_in.title');
  const message = useTranslation('magic_link.expired.message');
  const successTitle = useTranslation('magic_link.generate.success.title');

  return !authState?.isAuthenticated ? (
    <Page>
      {isSuccess ? (
        <Result
          icon={
            <IconShell
              icon={<CheckmarkFilled32 color="var(--success-color)" />}
            />
          }
          title={successTitle}
          subTitle={<Email>{email}</Email>}
          extra={[<GenerateNotifyMessage key={1} />]}
        />
      ) : (
        <Result
          icon={
            <IconShell
              icon={
                messageType === MagicLinkMessageType.NotSignedIn ? (
                  <Login32 />
                ) : (
                  <MagicWandFilled32 />
                )
              }
            />
          }
          title={
            messageType === MagicLinkMessageType.NotSignedIn
              ? notSignedInTitle
              : expiredTitle
          }
          subTitle={message}
          extra={[
            <GenerateLinkForm
              key={1}
              isLoading={loading}
              onSubmit={(_email) => {
                setEmail(_email);

                return run(_email);
              }}
            />,
          ]}
        />
      )}
    </Page>
  ) : (
    <Navigate replace to={RoutePaths.home} />
  );
};
